import Button from '@/components/ui/Button'
import { motion } from 'framer-motion'
import TextGenerateEffect from './TextGenerateEffect'
import { MODE_DARK, MODE_LIGHT } from '@/constants/theme.constant'
import { router } from '@inertiajs/react'
import type { Mode } from '@/@types/theme'
import { Link } from 'react-scroll'
import MobileAuthButtons from '@/components/pages/Landing/components/MobileAuthButtons';

const HeroContent = ({ mode }: { mode: Mode }) => {
    const handleStartFree = () => {
        // TODO: confirm canonical early-access signup route (currently /register)
        router.visit('/register')
    }

    return (
        <div className="max-w-7xl mx-auto px-4 flex min-h-screen flex-col items-center justify-between">
            <div className="flex flex-col min-h-screen pt-20 md:pt-40 relative overflow-hidden">
                <MobileAuthButtons />

                <div>
                    {/* First line */}
                    <TextGenerateEffect
                        wordClassName="text-2xl md:text-4xl lg:text-7xl font-bold max-w-7xl mx-auto text-center mt-6 relative z-10"
                        words="Shape rough thoughts into"
                        wordsCallbackClass={() => ''}
                    />
                    {/* Second line with highlight and no space between words */}
                    <TextGenerateEffect
                        className="-mt-2 md:-mt-3"
                        wordClassName="text-2xl md:text-4xl lg:text-7xl font-bold max-w-7xl mx-auto text-center relative z-10 tracking-tight"
                        words="clear notes"
                        wordsCallbackClass={() => ''}
                    />
                    <motion.p
                        initial={{ opacity: 0, translateY: 40 }}
                        animate={{ opacity: 1, translateY: 0 }}
                        transition={{ duration: 0.3, delay: 0.5 }}
                        className="text-center mt-6 text-base md:text-xl text-muted dark:text-muted-dark max-w-5xl mx-auto relative z-10 font-normal"
                    >
                        <span
                            className="inline-flex items-baseline whitespace-nowrap tracking-tight"
                            aria-label="MemoSlate"
                        >
                            <span className="text-primary">Memo</span>
                            <span className="bg-gradient-to-r from-primary to-rose-500 bg-clip-text text-transparent">
                                Slate
                            </span>
                        </span>{' '}
                        is an early-access workspace for capturing raw ideas, project
                        notes, checklists, temporary links, and sensitive snippets
                        before they are ready for a task manager, wiki, or formal
                        document.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, translateY: 40 }}
                        animate={{ opacity: 1, translateY: 0 }}
                        transition={{ duration: 0.3, delay: 0.6 }}
                        className="flex items-center gap-4 justify-center mt-10 relative z-10"
                    >
                        <Button variant="solid" onClick={handleStartFree}>
                            Start free
                        </Button>
                        <Link
                          smooth
                          to="earlyAccess"
                          duration={500}
                          className="inline-flex items-center justify-center h-12 px-5 py-2 rounded-xl border border-gray-300 text-gray-600 dark:text-gray-100 bg-white dark:bg-gray-700 dark:border-gray-700 transition-colors ring-primary dark:ring-white hover:border-primary dark:hover:border-white hover:ring-1 hover:text-primary dark:hover:text-white dark:hover:bg-transparent cursor-pointer whitespace-nowrap"
                        >
                            What's included
                        </Link>
                    </motion.div>
                    <motion.p
                        initial={{ opacity: 0, translateY: 20 }}
                        animate={{ opacity: 1, translateY: 0 }}
                        transition={{ duration: 0.3, delay: 0.7 }}
                        className="text-center mt-4 text-xs md:text-sm text-muted dark:text-muted-dark max-w-2xl mx-auto relative z-10"
                    >
                        Early access: core note editing, autosave, note history, and
                        encrypted secret fields. Some organization and collaboration
                        features are still evolving.
                    </motion.p>
                </div>
                <div className="p-2 lg:p-4 border border-gray-200 bg-gray-50 dark:bg-gray-700 dark:border-gray-700 rounded-2xl lg:rounded-[32px] mt-20 relative">
                    <div className="absolute inset-x-0 bottom-0 h-40 w-full bg-gradient-to-b from-transparent via-white to-white dark:via-black/50 dark:to-black scale-[1.1] pointer-events-none" />
                    <div className="bg-white dark:bg-black dark:border-gray-700 border border-gray-200 rounded-[24px]">
                        {mode === MODE_LIGHT && (
                            <img
                                className="rounded-2xl lg:rounded-[24px]"
                                src="/img/landing/hero/hero.png"
                                width={1920}
                                height={1040}
                                alt="Memo Slate screenshot"
                            />
                        )}
                        {mode === MODE_DARK && (
                            <img
                                className="rounded-2xl lg:rounded-[24px]"
                                src="/img/landing/hero/hero-dark.png"
                                width={1920}
                                height={1040}
                                alt="Memo Slate screenshot"
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default HeroContent
